/* eslint-disable react/no-unused-state */
import { forwardRef, createRef, PureComponent } from "react";

import { NotImplementedError } from "common/helpers/errors";

import { getters } from "common/components/getters/getters";
import { ErrorView } from "common/components/error";
import { Modal } from "common/components/modal";
import { Paginator } from "common/components/paginator/paginator";
import { WebixDatatable } from "common/components/webix/react/Datatable";
import { TablePlaceholder } from "common/components/placeholders";

import { TableConfig } from "./tableConfig";
import { TableControls, ExportWarningMessage } from "./tableControls";
import "./table.scss";

export class ConfigurableTable extends PureComponent {
    static defaultProps = {
        queryParams: {},
        showControls: true,
        showExportButton: true,
        showConfigButton: true,
        showCollapsedToggle: true,
        paginatorTop: false,
        paginatorBottom: false
    };

    static WebixDatatable = forwardRef((props, ref) => <WebixDatatable {...props} ref={ref} />);

    static TablePlaceholder = () => <TablePlaceholder columns={5} rows={2} />;

    static Paginator = (props) => <Paginator {...props} />;

    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            isLoading: true,
            page: 1,
            count: 0,
            data: [],
            config: [],
            error: null,
            configVisible: false
        };
        this.table_ref = createRef();
        this.promises = [];
    }

    componentDidMount() {
        this.fetchTableConfig();
        this.fetchData();
    }

    componentWillUnmount() {
        this.promises.forEach((promise) => promise?.cancel());
        this.table_ref = null;
        this.promises = null;
    }

    exportTable = (e) => {
        throw new NotImplementedError();
    };

    getEndpointUrl = () => {
        throw new NotImplementedError();
    };

    fetchTableConfig = () => {
        throw new NotImplementedError();
    };
    
    fetchData = (options = {}) => {
        throw new NotImplementedError();
    };

    actionWrapper = function(action, preventDefault = false) {
        return function(e, id, node) {
            if (preventDefault) e.preventDefault();
            const obj = this.getItem(id);
            const columnConfig = this.getColumnConfig(id?.column);
            action({
                id, obj, columnConfig, webix: this
            });
            return false;
        };
    };

    getUiOptions = () => {};

    getOnEvents = () => {};

    getWebixConf = ({
        noDataErrorProps = {
            header: "No data to display.",
            message: "We couldn't find any records for your request",
            options: {}
        },
        errorProps = {
            altError: new Error("Something went wrong. Try again."),
            options: {}
        }
    } = {}) => {
        const self = this;
        return {
            view: "datatable",
            on: {
                ...this.getOnEvents(),
                onAfterLoad() {
                    this.hideOverlay();
                    
                    if (self.state.hasError) {
                        this.showOverlay(
                            <ErrorView
                                error={self.state.error || errorProps.altError}
                                custom
                                hideDetails
                                hideConfig
                                {...errorProps.options}
                            />
                        );
                        return;
                    }
                    
                    if (!this.count()) {
                        this.config.columns.forEach((col) => {
                            if (col.id !== "control") this.adjustColumn(col.id, "header");
                        });
                        this.showOverlay(
                            <ErrorView
                                header={noDataErrorProps.header}
                                message={noDataErrorProps.message}
                                captureInSentry={false}
                                custom
                                hideDetails
                                hideConfig
                                actions={false}
                                {...noDataErrorProps.options}
                            />
                        );
                    }

                    if (self.getOnEvents().onAfterLoad) {
                        self.getOnEvents().onAfterLoad.call(this);
                    }
                }
            },
            ...this.getUiOptions(),
            columns: [
                ...this.getControlColumn(),
                ...this.getColumnsFromConfig({ forceFullview: this.props.forceFullView })
            ]
        };
    };

    getColumnsFromConfig = () => this.state.config
        .map((column) => {
            const getter = getters.findById(column.getter_ref);
            return {
                ...column,
                id: getter.key,
                header: getter.name
            };
        });

    getControlColumn = () => [];

    pageHandler = (page) => {
        this.fetchData({ page });
    };

    showConfig = () => this.setState({ configVisible: true });

    hideConfig = () => this.setState({ configVisible: false });

    handleConfigSave = (config) => { throw new NotImplementedError(); };

    getPazienConfig = () => { throw new NotImplementedError(); };

    toggleView = () => this.setState((prevState) => ({ fullView: !prevState.fullView }));
    
    isOverThreshold = () => {};

    renderControls = () => (
        <>
            <ExportWarningMessage millionRows={this.isOverThreshold()} />
            <TableControls
                expanded={this.state.fullView}
                toggleView={this.toggleView}
                showConfig={this.showConfig}
                export={this.exportTable}
                paginatorTop={this.props.paginatorTop}
                loading={this.state.isLoading}
                showExportButton={this.props.showExportButton}
                showConfigButton={this.props.showConfigButton}
                showCollapsedToggle={this.props.showCollapsedToggle}
                disableToolbar={this.state.data.length === 0}
                paginator={(
                    <Paginator
                        page={this.state.page}
                        count={this.state.count}
                        next={this.state.next}
                        loading={this.state.isLoading}
                        limit={100}
                        onPageChange={this.pageHandler}
                        results_length={this.state.data.length}
                    />
                )}
            />
        </>
    );

    renderConfig = () => (
        <TableConfig
            config={this.state.config}
            saveConfig={this.handleConfigSave}
            reset={this.handleConfigReset}
            section={this.props.section}
            getPazienConfig={this.getPazienConfig}
        />
    );

    render() {
        const ui = this.getWebixConf();
        const { page, count, next, isLoading } = this.state;
        return (
            <div className="configurable-table-view">
                {this.props.showConfigButton && (
                <Modal
                    close={this.hideConfig}
                    header="Table options"
                    portalId="modal-config-table"
                    show={this.state.configVisible}
                >
                    {this.renderConfig()}
                </Modal>
                )}
                {this.props.showControls && this.renderControls()}
                {isLoading ? <TablePlaceholder columns={5} rows={2} /> : <WebixDatatable className="configurable-table" ref={this.table_ref} ui={ui} data={this.state.data} />}
                {this.props.paginatorBottom
                    && (
                    <Paginator
                        page={page}
                        count={count}
                        next={next}
                        loading={isLoading}
                        limit={100}
                        onPageChange={this.pageHandler}
                        results_length={this.state.data.length}
                    />
                    )}
            </div>
        );
    }
}
